import { ServicePageTemplate, CapabilitiesGrid, AICapabilitiesSection, WhyChooseUsSection, FAQSection } from "@/components/templates";
import { createServiceSchema, createFAQSchema } from "@/utils/structuredData";
import {
  Code2,
  Globe,
  Database,
  Cloud,
  Shield,
  Zap,
  Users,
  Smartphone,
  Server,
  Rocket,
  HeartHandshake,
  Clock,
  Activity,
  BarChart3,
  Monitor,
  CheckCircle2,
  TrendingUp,
  Award,
  Brain,
  Eye,
  Layers,
  Boxes,
  Plug,
  TabletSmartphone,
} from "lucide-react";
import { DoubleDiamondProcess } from "@/components/DoubleDiamondProcess";
import { HelpCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

// FAQ items for Custom Development
const customDevFaqItems = [
  {
    question: "What types of custom software do you build?",
    answer: "We build complete software solutions including cross-platform mobile apps (iOS & Android), progressive web applications, desktop apps for Windows/macOS/Linux, custom backend systems, RESTful & GraphQL APIs, and microservices architectures. Whether you need a simple tool or a complex enterprise platform, we've got you covered.",
  },
  {
    question: "How long does custom development typically take?",
    answer: "Project timelines vary based on complexity. Simple applications take 4-8 weeks, medium complexity projects take 2-4 months, and large enterprise solutions take 4-6+ months. We provide detailed timelines after our discovery phase and keep you updated throughout development.",
  },
  {
    question: "Do you use agile development methodologies?",
    answer: "Yes! We follow agile practices with 2-week sprints, regular demos, and continuous feedback loops. You'll have visibility into progress at every stage and can provide input to steer the development in the right direction.",
  },
  {
    question: "Can you integrate AI capabilities into my application?",
    answer: "Absolutely! We specialize in embedding AI features including chatbots, computer vision, predictive analytics, and natural language processing. We work with OpenAI, TensorFlow, and custom ML models to add intelligent automation to your software.",
  },
  {
    question: "What about ongoing maintenance and support?",
    answer: "We offer comprehensive post-launch support including bug fixes, performance monitoring, security updates, and feature enhancements. We can be your long-term technology partner as your business grows and evolves.",
  },
  {
    question: "Do you work with existing codebases?",
    answer: "Yes, we regularly work with existing systems. Whether you need to modernize legacy applications, add new features to existing products, or integrate with third-party systems, our team can work with your current technology stack.",
  },
];

// Development capabilities data
const capabilities = [
  {
    title: "Cross-Platform Mobile Apps",
    description: "Build once, deploy everywhere. iOS and Android apps from a single codebase with native performance, offline support, and seamless app store deployment.",
    icon: TabletSmartphone,
    examples: ["iOS & Android Apps", "Tablet Optimization", "Offline-First", "Push Notifications", "App Store Submission"],
    gradient: "from-pink-500 to-rose-500",
  },
  {
    title: "Progressive Web Applications",
    description: "Fast, installable web apps that work offline. Combine the reach of the web with the experience of native apps — no app store required.",
    icon: Globe,
    examples: ["Installable Web Apps", "Offline Support", "Push Notifications", "SEO Optimized", "Cross-Browser"],
    gradient: "from-blue-500 to-cyan-500",
  },
  {
    title: "Desktop Applications",
    description: "Cross-platform desktop apps for Windows, macOS, and Linux. Perfect for enterprise tools, productivity software, and applications needing native system access.",
    icon: Monitor,
    examples: ["Windows, macOS, Linux", "Native Integration", "Auto-Updates", "Offline Capable", "System Tray Apps"],
    gradient: "from-purple-500 to-violet-500",
  },
  {
    title: "Custom Backend Systems",
    description: "Secure, scalable backend infrastructure tailored to your needs. From simple REST APIs to complex event-driven architectures built for millions of users.",
    icon: Server,
    examples: ["RESTful & GraphQL APIs", "Real-time WebSockets", "Authentication Systems", "Database Design", "Caching Layers"],
    gradient: "from-emerald-500 to-green-500",
  },
  {
    title: "API Development & Integration",
    description: "Connect your product to any service. We build robust APIs and integrate with payment gateways, CRMs, AI services, and enterprise systems.",
    icon: Plug,
    examples: ["Stripe & Razorpay", "Twilio & SendGrid", "Salesforce & HubSpot", "OpenAI & AWS AI", "Custom Webhooks"],
    gradient: "from-orange-500 to-amber-500",
  },
  {
    title: "Microservices Architecture",
    description: "For complex, high-scale applications. Independent services that scale separately, deploy individually, and evolve with your business needs.",
    icon: Boxes,
    examples: ["Service Mesh", "Container Orchestration", "Event-Driven Design", "API Gateway", "Kubernetes"],
    gradient: "from-indigo-500 to-blue-500",
  },
];

// AI capabilities data
const aiCapabilities = [
  {
    title: "AI Assistants & Chatbots",
    description: "Intelligent conversational AI that understands context, answers questions, and automates customer support with human-like interactions.",
    icon: Brain,
    useCases: ["Customer Support Bots", "Internal Knowledge Assistants", "Lead Qualification", "FAQ Automation"],
  },
  {
    title: "Computer Vision",
    description: "Image recognition, object detection, and visual analysis. From quality inspection to medical imaging and document processing.",
    icon: Eye,
    useCases: ["Product Recognition", "Quality Control", "Document OCR", "Medical Imaging"],
  },
  {
    title: "Predictive Analytics",
    description: "Machine learning models that forecast trends, detect anomalies, and power data-driven decision making for your business.",
    icon: TrendingUp,
    useCases: ["Demand Forecasting", "Churn Prediction", "Fraud Detection", "Price Optimization"],
  },
];

const CustomDevelopment = () => {
  const navigate = useNavigate();
  const serviceSchema = createServiceSchema({
    name: "Custom Development Services",
    description:
      "Full-stack custom software development: cross-platform mobile apps, web applications, desktop apps, custom backends, API development, microservices, and AI integration. Enterprise-grade solutions built for scale.",
    url: "https://interestech.com/services/custom-development",
    serviceType: "Custom Software Development",
    areaServed: "Worldwide",
  });

  // FAQ Schema for rich snippets
  const faqSchema = createFAQSchema(customDevFaqItems);

  return (
    <ServicePageTemplate
      seo={{
        title: "Custom Development | Web, Mobile, Desktop & API Development | Interesting Technologies",
        description:
          "Full-stack custom software development: cross-platform mobile apps (iOS & Android), web applications, desktop apps, custom backends, API development & integration, microservices architecture, and AI-powered solutions. Enterprise-grade software built for scale.",
        keywords:
          "custom software development, cross-platform mobile app development, web application development, desktop app development, API development, custom backend development, microservices architecture, React Native development, Flutter development, full-stack development, AI integration, enterprise software, bespoke software solutions",
        canonical: "https://interestech.com/services/custom-development",
        structuredData: [serviceSchema, faqSchema],
        ogImage: "https://interestech.com/itpl-og.webp",
        ogTitle: "Custom Development | Web, Mobile, Desktop & APIs | Interesting Technologies",
        ogDescription: "Cross-platform mobile apps, web apps, desktop apps, custom APIs & backends. Enterprise-grade custom software built for scale.",
      }}
      hero={{
        badge: { icon: Code2, text: "Full-Stack Development Partner" },
        title: "Custom Software",
        titleHighlight: "Built Your Way",
        description:
          "Cross-platform mobile apps. Web applications. Desktop software. Custom APIs. Scalable backends. AI integration. We build complete software ecosystems tailored to your unique business needs — from startup MVPs to enterprise platforms.",
        primaryCTA: { text: "Start Your Project", onClick: () => navigate('/contact') },
        secondaryCTA: { text: "View Our Work", onClick: () => navigate('/our-work') },
        gradient: "from-slate-900 via-blue-900 to-slate-900",
        accentColor: "blue",
        orbitIcons: [TabletSmartphone, Globe, Server, Boxes, Plug, Brain],
      }}
      stats={{
        items: [
          { label: "Projects Delivered", value: 250, suffix: "+", icon: Rocket },
          { label: "Client Satisfaction", value: 98, suffix: "%", icon: HeartHandshake },
          { label: "Expert Developers", value: 50, suffix: "+", icon: Users },
          { label: "Support Available", value: 24, suffix: "/7", icon: Clock },
        ],
        gradient: "from-blue-500 to-cyan-500",
      }}
      features={{
        title: "Complete Development Capabilities",
        subtitle: "From mobile apps to enterprise backends — one team handles your entire tech stack with precision and expertise.",
        badge: { icon: Layers, text: "Full-Stack Expertise", color: "blue" },
        items: [
          { 
            text: "Cross-Platform Mobile Apps", 
            description: "iOS and Android apps from a single codebase using Flutter or React Native. Native performance, offline support, push notifications.",
            icon: TabletSmartphone, 
            gradient: "from-pink-500 to-rose-500" 
          },
          { 
            text: "Web Applications & PWAs", 
            description: "Responsive web apps and Progressive Web Apps using React, Vue, or Angular. Fast, SEO-optimized, and installable.",
            icon: Globe, 
            gradient: "from-blue-500 to-cyan-500" 
          },
          { 
            text: "Custom Backend & APIs", 
            description: "Scalable REST/GraphQL APIs, authentication systems, real-time features, and database design. Your backend, built your way.",
            icon: Server, 
            gradient: "from-emerald-500 to-green-500" 
          },
          { 
            text: "Desktop Applications", 
            description: "Cross-platform desktop apps for Windows, macOS, and Linux using Electron or Tauri. Native system integration included.",
            icon: Monitor, 
            gradient: "from-purple-500 to-violet-500" 
          },
          { 
            text: "AI & Machine Learning", 
            description: "Intelligent features powered by OpenAI, TensorFlow, and custom models. Chatbots, computer vision, predictive analytics.",
            icon: Brain, 
            gradient: "from-orange-500 to-red-500" 
          },
          { 
            text: "Microservices Architecture", 
            description: "Scalable, maintainable systems with independent services. Kubernetes, Docker, and event-driven design for enterprise needs.",
            icon: Boxes, 
            gradient: "from-indigo-500 to-blue-500" 
          },
        ],
      }}
      technologies={{
        title: "Modern Tech Stack",
        subtitle: "We use the same technologies as top tech companies — ensuring your software is future-proof and maintainable.",
        badge: { icon: Database, text: "Technologies", color: "blue" },
        items: [
          { 
            name: "Frontend", 
            description: "Beautiful, responsive interfaces with modern frameworks",
            tools: ["React", "Next.js", "Vue", "TypeScript"] 
          },
          { 
            name: "Backend", 
            description: "Scalable server-side solutions and APIs",
            tools: ["Node.js", "Python", "Go", "GraphQL"] 
          },
          { 
            name: "Mobile", 
            description: "Cross-platform and native mobile development",
            tools: ["React Native", "Flutter", "Swift", "Kotlin"] 
          },
          { 
            name: "AI/ML", 
            description: "Intelligent automation and predictive models",
            tools: ["OpenAI", "TensorFlow", "LangChain", "RAG"] 
          },
          { 
            name: "Cloud & DevOps", 
            description: "Enterprise-grade cloud infrastructure",
            tools: ["AWS", "GCP", "Docker", "Kubernetes"] 
          },
          { 
            name: "Database", 
            description: "High-performance data storage solutions",
            tools: ["PostgreSQL", "MongoDB", "Redis", "Firebase"] 
          },
        ],
        checkColor: "text-blue-500",
      }}
      industries={{
        badge: { icon: TrendingUp, text: "Industries", color: "blue" },
        title: "Industries We Serve",
        subtitle: "Deep domain expertise across multiple industries with tailored software solutions.",
        items: [
          { name: "Healthcare & Telemedicine", icon: Activity, projects: 35, percentage: 88 },
          { name: "Finance & FinTech", icon: BarChart3, projects: 42, percentage: 92 },
          { name: "E-commerce & Retail", icon: Globe, projects: 58, percentage: 95 },
          { name: "Education & EdTech", icon: Monitor, projects: 28, percentage: 85 },
        ],
        progressGradient: "from-blue-500 to-cyan-500",
        progressIconColor: "text-blue-500",
        statBoxes: [
          {
            icon: CheckCircle2,
            value: "250+",
            label: "Projects",
            gradient: "from-blue-500 to-cyan-500",
            iconColor: "text-white",
          },
          {
            icon: Users,
            value: "50+",
            label: "Developers",
            gradient: "from-purple-500 to-pink-500",
            iconColor: "text-white",
          },
          {
            icon: Award,
            value: "98%",
            label: "Satisfaction",
            gradient: "from-green-500 to-emerald-500",
            iconColor: "text-white",
          },
          {
            icon: Clock,
            value: "24/7",
            label: "Support",
            gradient: "from-orange-500 to-red-500",
            iconColor: "text-white",
          },
        ],
      }}
      cta={{
        title: "Ready to Build Your Custom Software?",
        description: "From mobile apps to enterprise backends — let's discuss how we can bring your vision to life with the right technology.",
        buttonText: "Start Your Project",
        buttonOnClick: () => navigate('/contact'),
        gradient: "from-blue-600 via-indigo-600 to-purple-600",
        buttonTextColor: "blue",
      }}
    >
      {/* Double Diamond Process Section */}
      <DoubleDiamondProcess />

      {/* What We Build - Detailed Capabilities Section */}
      <CapabilitiesGrid
        badge={{ icon: Layers, text: "Development Capabilities", color: "blue" }}
        title="Everything You Need to"
        titleHighlight="Build & Scale"
        subtitle="We don't just write code — we build complete software ecosystems. From mobile apps to enterprise backends, one team handles everything."
        items={capabilities}
        hoverColor="blue"
      />

      {/* AI Capabilities Section */}
      <AICapabilitiesSection
        badge={{ icon: Brain, text: "AI-Powered Features" }}
        title="Embed"
        titleHighlight="AI Intelligence Into Your Software"
        subtitle="Add AI capabilities to any application — chatbots, computer vision, predictive analytics, and more. We make AI accessible and practical."
        items={aiCapabilities}
        accentColor="purple"
      />

      {/* Why Choose Us Section */}
      <WhyChooseUsSection
        title="Why Build Your Entire Stack With Us?"
        subtitle="Fragmented development teams lead to fragmented products. One team = faster delivery, lower cost, seamless integration."
        stats={[
          { value: "50%", label: "Faster than multiple vendors" },
          { value: "Zero", label: "Integration headaches" },
          { value: "1 Team", label: "Full accountability" },
          { value: "24/7", label: "Support & maintenance" },
        ]}
        accentColor="blue"
        cta={{
          text: "Need a rapid MVP instead of a full custom build?",
          link: "/services/mvp-development",
          linkText: "Check out our 2-3 week MVP Development service",
        }}
      />

      {/* FAQ Section */}
      <FAQSection
        badge={{ icon: HelpCircle, text: "FAQ", color: "blue" }}
        title="Frequently Asked Questions"
        subtitle="Everything you need to know about our custom development services."
        items={customDevFaqItems}
        accentColor="blue"
        contactCTAText="Still have questions? We're here to help!"
        contactCTAButtonText="Start Your Project"
        onContactClick={() => navigate('/contact')}
      />
    </ServicePageTemplate>
  );
};

export default CustomDevelopment;